import {
  CreateBrainSearchInput,
  useStartNewBrainSearchMutation,
} from "@/admin/integrations/chat-bot/util/__generated__/useStartNewBrainSearchMutation.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { GlobalDrawerParams, useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import FormStore, { useFormStore } from "@/core/form/store/FormStore"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import RelayEnvironment from "@/relay/RelayEnvironment"
import { GlobalID } from "@/relay/RelayTypes"
import { useCallback } from "react"
import { ConnectionHandler, commitLocalUpdate, graphql } from "react-relay"
import ConnectionHandlerPlus from "relay-connection-handler-plus"
export type CreateBrainSearchFormStore = FormStore<
  CreateBrainSearchInput,
  useStartNewBrainSearchMutation
>

type Opts = {
  isSidePanel?: boolean
  contentUsageId?: GlobalID
  onCompleted?: (brainSearchId: string) => void
}

function useStartNewBrainSearch(opts: Opts = {}) {
  const { isSidePanel = false, contentUsageId, onCompleted } = opts
  const drawer = useGlobalDrawer("brainSearch")
  const activeOrganization = useActiveOrganization()!

  const form = useFormStore<useStartNewBrainSearchMutation>(
    graphql`
      mutation useStartNewBrainSearchMutation(
        $input: CreateBrainSearchInput!
        $connections: [ID!]!
      ) {
        response: createBrainSearch(input: $input) {
          node
            @prependNode(connections: $connections, edgeTypeName: "BrainSearchNodeEdge") {
            id
            contentUsage {
              id
            }
            ...BrainSearchSidebarList_ItemFragment @relay(mask: false)
          }
          errors {
            field
            message
          }
        }
      }
    `,
    { title: "", contentUsageId }
  )

  const handleSubmit = useCallback(
    async (inputOverride?: string) => {
      const prompt = inputOverride || form.state.title
      if (!prompt) return

      const { response, didSave } = await form.submit(
        { ...form.state, title: prompt },
        {
          connections: [
            ConnectionHandler.getConnectionID(
              activeOrganization.viewerMembership!.id,
              "BrainSearchSidebarList__brainSearches"
            ),
          ],
        }
      )
      if (!didSave || !response?.node?.id) return

      // Invalidate PastUserBrainSearches connections when a new brain search is created
      commitLocalUpdate(RelayEnvironment, (store) => {
        const targetContentUsageId =
          response.node?.contentUsage?.id || form.state.contentUsageId

        if (!targetContentUsageId) return
        const contentUsageRecord = store.get(targetContentUsageId)
        if (!contentUsageRecord) return

        ConnectionHandlerPlus.getConnections(
          contentUsageRecord,
          "PastUserBrainSearches__viewerBrainSearches"
        ).forEach((connection) => connection.invalidateRecord())
      })

      // Navigate to the conversation in the drawer with a prompt to start generation
      const params: Partial<GlobalDrawerParams<"brainSearch">> = {
        convId: response.node.id,
        prompt: encodeURIComponent(prompt),
      }
      if (location.pathname !== ROUTE_NAMES.COMMUNITY.WEBVIEW.BRAIN_SEARCH) {
        // Don't open the global drawer if brainSearch is embedded in a webview page
        params[isSidePanel ? "drawerAsk" : "ask"] = "1"
      }
      drawer.open(params)

      onCompleted?.(response.node.id)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeOrganization.viewerMembership, form]
  )

  return [form, handleSubmit] as const
}

export default useStartNewBrainSearch
